import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, MapPin, Download, Share2, FileText, X, Send, Upload, Trash2, LayoutDashboard, CalendarClock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { API_URL, formatApiError } from "@/lib/api";
import { STATO_STYLES, tipoLabel, statoLabel, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useStudio } from "@/context/StudioContext";

function PortalFileActions({ url, filename, title }) {
  const [busy, setBusy] = useState(false);
  const getFile = async () => {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) throw new Error("Download non riuscito");
    const blob = await response.blob();
    return new File([blob], filename || "documento", { type: blob.type || "application/octet-stream" });
  };
  const share = async () => {
    setBusy(true);
    try {
      const file = await getFile();
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ title: title || filename, files: [file] });
      } else {
        const objectUrl = URL.createObjectURL(file); const anchor = document.createElement("a");
        anchor.href = objectUrl; anchor.download = file.name; anchor.click(); setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (error) { if (error?.name !== "AbortError") toast.error("Impossibile condividere il file"); }
    finally { setBusy(false); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const file = await getFile(); const objectUrl = URL.createObjectURL(file); const anchor = document.createElement("a");
      anchor.href = objectUrl; anchor.download = file.name; document.body.appendChild(anchor); anchor.click(); anchor.remove(); setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch { toast.error("Impossibile scaricare il file"); }
    finally { setBusy(false); }
  };
  return <div className="flex items-center gap-2 shrink-0">
    <button type="button" disabled={busy} title="Condividi o salva sul telefono" aria-label="Condividi o salva sul telefono" onClick={share} className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100 hover:text-blue-900 disabled:opacity-40"><Share2 className="h-4 w-4" /></button>
    <button type="button" disabled={busy} title="Scarica" aria-label="Scarica documento" onClick={download} className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100 hover:text-blue-900 disabled:opacity-40"><Download className="h-4 w-4" /></button>
  </div>;
}

export default function PortaleCliente() {
  const { settings } = useStudio();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [activeSection, setActiveSection] = useState("panoramica");
  const [installGuideVisible, setInstallGuideVisible] = useState(() => {
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    return !standalone;
  });
  const [mobilePlatform] = useState(() => {
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
    return "other";
  });

  const load = () => {
    axios
      .get(`${API_URL}/portal/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  };
  const uploadDocument = async (e) => { e.preventDefault(); if(!uploadFile) return; const fd=new FormData(); fd.append("file",uploadFile); fd.append("title",uploadTitle); try { await axios.post(`${API_URL}/portal/${token}/documents`,fd); toast.success("Documento inviato allo studio"); setUploadFile(null); setUploadTitle(""); e.target.reset(); load(); } catch(err) { toast.error(formatApiError(err)); } };
  const deleteDocument = async (a) => { if(!window.confirm(`Eliminare il documento "${a.title || a.filename}"?`)) return; try { await axios.delete(`${API_URL}/portal/${token}/documents/${a.id}`); toast.success("Documento eliminato"); load(); } catch(err) { toast.error(formatApiError(err)); } };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const manifest = document.querySelector('link[rel="manifest"]');
    const previousHref = manifest?.getAttribute("href");
    if (manifest) manifest.setAttribute("href", `${API_URL}/portal/${token}/manifest.webmanifest`);
    return () => { if (manifest && previousHref) manifest.setAttribute("href", previousHref); };
  }, [token]);

  const sendComment = async (e) => {
    e.preventDefault();
    if (!author.trim() || !text.trim()) {
      toast.error("Inserisci nome e messaggio");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/portal/${token}/comments`, { author, text });
      setText("");
      toast.success("Messaggio inviato allo studio");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="portal-invalid">
        <div className="text-center">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold text-slate-900">Link non valido</h1>
          <p className="text-sm text-slate-500 mt-2">Questo link di condivisione non esiste o è stato revocato. Contatta lo studio tecnico.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="portal-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-800 border-t-transparent" />
      </div>
    );
  }

  if (data.portal_suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="portal-suspended">
        {settings?.logo_url ? <img src={settings.logo_url} alt="Logo studio" className="w-full max-w-xs max-h-56 object-contain" /> : null}
      </div>
    );
  }

  const { practice, attachments, comments, client_name, portal_settings } = data;
  const customerDocs = attachments.filter((a) => a.category === "documento" && a.source === "cliente");
  const studioAttachments = attachments.filter((a) => a.category === "cliente");
  const docsCount = customerDocs.length + studioAttachments.length;
  const fileUrl = (a) => `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${a.url}`;
  const menu = [{id:"panoramica",label:"Panoramica",icon:LayoutDashboard,show:true},{id:"scadenze",label:"Scadenze",icon:CalendarClock,show:portal_settings.show_deadlines},{id:"documenti",label:"Documenti",icon:FileText,show:customerDocs.length > 0},{id:"allegati",label:"Allegati",icon:FileText,show:studioAttachments.length > 0},{id:"caricamenti",label:"Invia documenti",icon:Upload,show:portal_settings.allow_document_upload},{id:"messaggi",label:"Messaggi",icon:MessageSquare,show:portal_settings.allow_messages}].filter(i=>i.show);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal">
      <header style={{backgroundColor:settings?.sidebar_color || '#0f172a'}} className="text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 mb-8 border-b border-slate-800 pb-7">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            {settings?.logo_url ? <img src={settings.logo_url} alt="Logo" className="h-16 sm:h-24 w-28 sm:w-48 shrink-0 object-contain bg-white rounded-lg p-2" /> : <div className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-lg bg-slate-800 flex items-center justify-center"><Building2 className="h-8 w-8 text-amber-500" /></div>}
            <span className="min-w-0 break-words font-heading text-base sm:text-2xl font-extrabold tracking-tight">{settings?.studio_name || "CLGGEST"}</span>
            </div>
            <span className="text-black text-xs sm:text-sm font-mono uppercase tracking-[0.25em]">Portale Cliente</span>
          </div>
          <div className="grid md:grid-cols-[1fr_0.8fr] gap-7 md:gap-14 items-end">
          <div>
          {portal_settings.show_details && <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
              {practice.protocol_code}
            </span>
            <span style={{color:"#000000"}} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATO_STYLES[practice.status] || ""}`} data-testid="portal-status-badge">
              {statoLabel(practice.status)}
            </span>
            <span className="text-xs font-medium text-black">{tipoLabel(practice.type, settings?.practice_types)}</span>
          </div>}
          <h1 className="break-words font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="portal-practice-title">{practice.title}</h1>
          {portal_settings.show_details && <p className="text-sm text-black/70 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            {client_name && <span>{client_name}</span>}
            {practice.comune && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {practice.comune}{practice.address ? `, ${practice.address}` : ""}</span>}
          </p>}
          </div>
          {portal_settings.show_progress && practice.progress !== null && <div className="flex items-center gap-3 md:pb-1">
            <div className="flex-1 h-2.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${practice.progress}%` }} />
            </div>
            <span className="font-mono text-xs font-semibold text-black" data-testid="portal-progress">{practice.progress}% completato</span>
          </div>}
          </div>
        </div>
      </header>

      <main className="w-full min-w-0 max-w-7xl mx-auto px-3 sm:px-8 lg:px-10 py-5 sm:py-8">
        {installGuideVisible && mobilePlatform !== "other" && (
          <section className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left" data-testid="install-app-guide">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-heading text-sm font-bold text-slate-900">Aggiungi Arch. CLG alla schermata Home</h2>
                {mobilePlatform === "ios" ? (
                  <div className="mt-2 space-y-2 text-sm text-slate-700">
                    <p><strong>Con Safari:</strong> tocca il pulsante <strong>Condividi</strong> e scegli <strong>Aggiungi alla schermata Home</strong>.</p>
                    <p><strong>Con Chrome:</strong> tocca <strong>Condividi</strong>, scorri l’elenco delle azioni e scegli <strong>Aggiungi alla schermata Home</strong>.</p>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-slate-700">Apri questa pagina in <strong>Chrome</strong>, tocca il menu <strong>⋮</strong> e scegli <strong>Installa app</strong> oppure <strong>Aggiungi a schermata Home</strong>.</p>
                )}
              </div>
              <button type="button" onClick={() => setInstallGuideVisible(false)} className="shrink-0 rounded p-1 text-slate-500 hover:bg-blue-100" aria-label="Chiudi istruzioni"><X className="h-4 w-4" /></button>
            </div>
          </section>
        )}
        <div className="grid min-w-0 lg:grid-cols-[250px_1fr] gap-4 lg:gap-8 items-start">
          <aside style={{backgroundColor:settings?.sidebar_color || '#0f172a'}} className="sticky top-0 z-30 min-w-0 rounded-xl p-2 shadow-sm lg:top-6 lg:p-3"><p className="hidden px-3 pt-2 pb-3 text-[10px] text-white/60 font-mono uppercase tracking-widest lg:block">Menu pratica</p><nav className="flex min-w-0 gap-1 overflow-x-auto overscroll-x-contain pb-1 lg:flex-col lg:overflow-visible lg:pb-0">{menu.map(({id,label,icon:Icon})=><button key={id} onClick={()=>setActiveSection(id)} className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-sm whitespace-nowrap transition-colors lg:w-full lg:gap-3 ${activeSection===id?'bg-blue-800 text-white':'text-white/75 hover:bg-white/10 hover:text-white'}`}><Icon className="h-4 w-4 shrink-0"/>{label}</button>)}</nav></aside>
          <div className="space-y-6 min-w-0">
        {activeSection === "panoramica" && <div className="grid sm:grid-cols-2 gap-4"><div className="bg-white rounded-xl border p-5"><p className="text-xs text-slate-500">Documenti disponibili</p><p className="text-3xl font-extrabold text-blue-800 mt-2">{docsCount}</p></div><div className="bg-white rounded-xl border p-5"><p className="text-xs text-slate-500">Messaggi</p><p className="text-3xl font-extrabold text-blue-800 mt-2">{comments.length}</p></div></div>}
        {activeSection === "panoramica" && portal_settings.show_details && practice.description && (
          <section className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
            <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-2">Descrizione lavori</h2>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{practice.description}</p>
          </section>
        )}

        {activeSection === "scadenze" && portal_settings.show_deadlines && (
          <section className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="portal-deadlines">
            <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-4">Prossime scadenze</h2>
            {practice.deadlines?.length ? <ul className="divide-y divide-slate-100">
              {[...practice.deadlines].sort((a, b) => a.date.localeCompare(b.date)).map((d, i) => (
                <li key={i} className="py-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <p className="break-words text-sm font-medium text-slate-800">{d.title}</p>
                  <span className="font-mono text-xs font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap">
                    {formatDate(d.date)}
                  </span>
                </li>
              ))}
            </ul> : <p className="text-sm text-slate-500">Nessuna scadenza disponibile.</p>}
          </section>
        )}

        {activeSection === "documenti" && customerDocs.length > 0 && <section className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="portal-documents">
          <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-4">Documenti ({customerDocs.length})</h2>
          {customerDocs.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun documento disponibile.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {customerDocs.map((a) => (
                <li key={a.id} className="py-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="portal-documento-item">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-blue-800 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{a.title || a.filename}</p>
                      <p className="text-xs text-slate-500 truncate">File: {a.filename}</p>
                      <p className="text-xs text-slate-400">{formatDate(a.created_at)}{a.uploaded_by ? ` · Caricato da ${a.uploaded_by}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 sm:justify-start"><PortalFileActions url={fileUrl(a)} filename={a.filename} title={a.title}/>{portal_settings.allow_document_upload && a.source === "cliente" && <button onClick={()=>deleteDocument(a)} title="Elimina documento" aria-label="Elimina documento" className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600"><Trash2 className="h-4 w-4"/></button>}</div>
                </li>
              ))}
            </ul>
          )}
        </section>}
        {activeSection === "allegati" && studioAttachments.length > 0 && <section className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="portal-attachments">
          <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-4">Allegati ({studioAttachments.length})</h2>
          <ul className="divide-y divide-slate-100">
            {studioAttachments.map((a) => (
              <li key={a.id} className="py-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="portal-allegato-item">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 text-blue-800 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{a.title || a.filename}</p>
                    <p className="text-xs text-slate-500 truncate">File: {a.filename}</p>
                    <p className="text-xs text-slate-400">{formatDate(a.created_at)}{a.uploaded_by ? ` · Caricato da ${a.uploaded_by}` : ""}</p>
                  </div>
                </div>
                <div className="flex justify-end sm:justify-start"><PortalFileActions url={fileUrl(a)} filename={a.filename} title={a.title}/></div>
              </li>
            ))}
          </ul>
        </section>}
        {activeSection === "caricamenti" && portal_settings.allow_document_upload && <section className="bg-white rounded-xl border p-4 sm:p-6"><h2 className="font-heading text-lg font-semibold mb-4">Invia un documento allo studio</h2><form onSubmit={uploadDocument} className="min-w-0 space-y-3"><Input required placeholder="Nome del documento" value={uploadTitle} onChange={e=>setUploadTitle(e.target.value)}/><Input required type="file" className="h-auto min-h-11 py-2 file:mr-2 file:max-w-[55%]" onChange={e=>setUploadFile(e.target.files?.[0])}/><Button className="w-full bg-blue-800 sm:w-auto"><Upload className="h-4 w-4 mr-2"/>Carica documento</Button><p className="text-xs text-slate-500">Dimensione massima 20 MB.</p></form></section>}

        {activeSection === "messaggi" && portal_settings.allow_messages && <section className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="portal-comments">
          <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-4">Messaggi con lo studio</h2>
          {comments.length > 0 && (
            <ul className="space-y-3 mb-6">
              {comments.map((c) => (
                <li key={c.id} className={`rounded-lg border p-4 ${c.source === "cliente" ? "bg-amber-50/60 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid="portal-comment-item">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-700">{c.author}</span>
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${c.source === "cliente" ? "bg-amber-200 text-amber-900" : "bg-blue-100 text-blue-800"}`}>
                      {c.source === "cliente" ? "Tu" : "Studio"}
                    </span>
                    <span className="text-[11px] text-slate-400 sm:ml-auto">{formatDate(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.text}</p>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={sendComment} className="space-y-3">
            <Input
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Il tuo nome"
              data-testid="client-portal-name-input"
            />
            <Textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Scrivi un messaggio allo studio..."
              data-testid="client-portal-comment-input"
            />
            <Button type="submit" disabled={sending} className="w-full bg-blue-800 hover:bg-blue-900 sm:w-auto" data-testid="client-portal-send-comment-button">
              <Send className="h-4 w-4 mr-1.5" /> {sending ? "Invio..." : "Invia messaggio"}
            </Button>
          </form>
        </section>}

        <p className="text-center text-xs text-slate-400 pb-8">Portale cliente riservato · {settings?.studio_name || "CLGGEST"}</p>
          </div>
        </div>
      </main>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-2 sm:p-6" onClick={() => setLightbox(null)} data-testid="portal-lightbox">
          <button className="absolute top-3 right-3 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white" aria-label="Chiudi anteprima"><X className="h-7 w-7" /></button>
          <img src={fileUrl(lightbox)} alt={lightbox.filename} className="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}
